import {
    IOption,
    SearchableLocationForm,
    SearchPersonForm,
    SearchVehicleForm,
    SubPremiseDto,
} from '@mark43/cad-api';
import { compact } from 'lodash';
import { formatMonthDayYear } from '../common/helpers/utils';

export function formatOptions(label: string, options: IOption[]) {
    return formatString(label, options.map((option) => option.label).join(', '));
}

export function formatOption(label: string, option?: IOption) {
    return formatString(label, option?.label);
}

export function formatStrings(label: string, stringValues: (string | undefined)[]) {
    return formatString(
        label,
        stringValues
            .filter((value) => !!value)
            .map((value) => value?.trim())
            .join(', ')
    );
}

export function formatNumber(label: string, numberValue?: number) {
    if (numberValue !== undefined) {
        return formatString(label, `${numberValue}`);
    }
}

export function formatBoolean(label: string, booleanValue?: boolean) {
    if (booleanValue !== undefined) {
        return formatString(label, booleanValue ? 'Yes' : 'No');
    }
}

export function formatString(label: string, stringValue?: string) {
    if (stringValue && stringValue.trim().length > 0) {
        return `${label}: ${stringValue.trim()}`;
    }
}

export function formatSubpremises(subPremiseDtos: SubPremiseDto[]) {
    return subPremiseDtos
        .map((dto) => compact([dto.subPremiseName, dto.subPremiseValue]).join(' '))
        .join(', ');
}

export function formatLocations(locations: SearchableLocationForm[], subpremiseSupport: boolean) {
    return compact([
        ...locations
            .map((location) => [
                formatString('Street Address', location.streetAddress),
                subpremiseSupport
                    ? formatString('Sub-premises', formatSubpremises(location.subPremiseDtos))
                    : formatString('Apt, Unit', location.subPremise),
                formatOptions('Subdivisions', location.subdivisions),
                formatString('City', location.city),
                formatOption('State', location.state),
                formatString('ZIP', location.zip),
                formatOption('Country', location.country),
                formatString('Intersection Street 1', location.intersectionStreet1),
                formatString('Intersection Street 2', location.intersectionStreet2),
                formatNumber('Latitude', location.latitude),
                formatNumber('Longitude', location.longitude),
                formatNumber('Radius (Feet)', location.radiusInFeet),
            ])
            .flat(),
    ]).join(', ');
}

export function formatPersons(persons: SearchPersonForm[]) {
    return compact(
        persons
            .map((person) => [
                formatString('First Name', person.firstName),
                formatString('Last Name', person.lastName),
                formatOption('Race', person.race),
                formatOption('Sex', person.sex),
                formatString(
                    'Date of Birth',
                    person.dateOfBirth ? formatMonthDayYear(person.dateOfBirth) : undefined
                ),
                formatNumber('Min Age', person.ageMin),
                formatNumber('Max Age', person.ageMax),
                formatOption('Involvement Type', person.involvementType),
                formatString('Involvement Type Description', person.involvementTypeOther),
                formatString('DL #', person.driversLicenseNumber),
                formatOption('DL State', person.driversLicenseState),
                formatString('SSN', person.socialSecurityNumber),
                formatString('Notes', person.notes),
                formatString('Height', person.height),
                formatString('Min Height', person.heightMin),
                formatString('Max Height', person.heightMax),
                formatNumber('Weight', person.weight),
                formatNumber('Min Weight', person.weightMin),
                formatNumber('Max Weight', person.weightMax),
                formatOption('Eye Color', person.eyeColor),
                formatOption('Hair Color', person.hairColor),
                formatString('Phone', person.phoneNumber),
                formatString('Ext.', person.phoneExtension),
            ])
            .flat()
    ).join(', ');
}

export function formatVehicles(vehicles: SearchVehicleForm[]) {
    return compact(
        vehicles
            .map((vehicle) => [
                formatString('Plate #', vehicle.plateNumber),
                formatOption('Plate State', vehicle.state),
                formatString('VIN', vehicle.vinNumber),
                formatOption('Color', vehicle.color),
                formatOption('Make', vehicle.vehicleMake),
                formatString('Make Description', vehicle.vehicleMakeOther),
                formatOption('Model', vehicle.vehicleModel),
                formatString('Model Description', vehicle.vehicleModelOther),
                formatNumber('Year', vehicle.year),
                formatString('Notes', vehicle.notes),
                formatOption('Involvement Type', vehicle.involvementType),
                formatString('Involvement Type Description', vehicle.involvementTypeOther),
                formatString('Tow Type', vehicle.towType),
            ])
            .flat()
    ).join(', ');
}
